import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';

export interface DropdownOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './multi-select.html',
  styleUrl: './multi-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelect),
      multi: true,
    },
  ],
})
export class MultiSelect implements ControlValueAccessor, OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() allowSearch: boolean = false;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;

  // ✅ Updated output format to match checkbox component
  @Output() answerChange = new EventEmitter<{ 
    questionId: string; 
    options: { optionId: string; optionSelected: string }[]; 
  }>();

  options: DropdownOption[] = [];
  filteredOptions: DropdownOption[] = [];
  selectedOptions: DropdownOption[] = [];
  searchTerm = '';
  dropdownOpen = false;
  disabled = false;
  othersTextMap: Map<string, string> = new Map(); // Store text for each Others option
  private isUpdating = false; // Flag to prevent writeValue from overwriting during toggle
  private othersOptionIds: Set<string> = new Set(); // Cache all Others option IDs

  // ControlValueAccessor
  onChange = (value: string[]) => {};
  onTouched = () => {};

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  ngOnInit() {
    // Cache all Others option IDs for faster lookups
    this.othersOptionIds = this.findOthersOptionIds();
    
    this.options = this.question?.questionOptions?.map(opt => ({
      value: opt.optionId,
      label: opt.text
    })) || [];
    this.filteredOptions = [...this.options];

    // Only set initial values once, don't subscribe continuously.
    if (this.formControl?.value && Array.isArray(this.formControl.value)) {
      const initialValues = this.formControl.value;
      this.selectedOptions = this.options.filter(opt => initialValues.includes(opt.value));
    }
  }

  writeValue(value: string[]): void {
    // Don't overwrite if we're currently updating internally
    if (this.isUpdating) {
      return;
    }
    
    const previousValues = new Set(this.selectedOptions.map(opt => opt.value));
    
    if (value && Array.isArray(value)) {
      this.selectedOptions = this.options.filter(opt => value.includes(opt.value));
    } else {
      this.selectedOptions = [];
    }

    // Clear others text for options that are no longer selected
    const currentValues = new Set(this.selectedOptions.map(opt => opt.value));
    previousValues.forEach(optionId => {
      if (!currentValues.has(optionId) && this.othersOptionIds.has(optionId)) {
        this.othersTextMap.delete(optionId);
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.onTouched();
        if (this.formControl) {
          this.formControl.markAsTouched();
        }
      }
    }
  }

  selectOption(option: DropdownOption) {
    if (this.disabled) return;

    // Set flag to prevent writeValue from overwriting during update
    this.isUpdating = true;

    const index = this.selectedOptions.findIndex(opt => opt.value === option.value);
    if (index > -1) {
      // Unselect the option
      this.selectedOptions.splice(index, 1);
      // Clear others text if it was an Others option
      if (this.othersOptionIds.has(option.value)) {
        this.othersTextMap.delete(option.value);
      }
    } else {
      // Select the option
      this.selectedOptions.push(option);
    }

    this.updateValue();

    // Reset flag after a short delay to allow any async operations to complete
    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  removeOption(event: Event, option: DropdownOption) {
    event.stopPropagation(); // Prevent dropdown toggle
    if (this.disabled) return;

    // Set flag to prevent writeValue from overwriting during update
    this.isUpdating = true;

    const index = this.selectedOptions.findIndex(opt => opt.value === option.value);
    if (index > -1) {
      this.selectedOptions.splice(index, 1);
      // Clear others text if it was an Others option
      if (this.othersOptionIds.has(option.value)) {
        this.othersTextMap.delete(option.value);
      }
      this.updateValue();
    }

    // Reset flag after a short delay to allow any async operations to complete
    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  updateValue() {
    const selectedValues = this.selectedOptions.map(opt => opt.value);
    
    // ✅ Update form control without re-triggering valueChanges
    if (this.formControl) {
      this.formControl.setValue(selectedValues, { emitEvent: false });
      this.formControl.markAsTouched();
    }

    // ✅ Notify Angular forms - this might trigger writeValue, but our flag will prevent overwrite
    this.onChange(selectedValues);
    this.onTouched();

    // ✅ Emit payload with ALL currently selected options (matching checkbox format)
    this.emitAnswer();
  }

  private emitAnswer() {
    if (!this.question?.questionId) return;

    const selectedValues = this.selectedOptions.map(opt => opt.value);
    
    // Map ALL selected option IDs to the required format with optionSelected
    const formattedOptions = selectedValues.map(optionId => ({
      optionId,
      optionSelected: this.othersOptionIds.has(optionId) ? (this.othersTextMap.get(optionId) || '') : ''
    }));

    this.answerChange.emit({
      questionId: this.question.questionId,
      options: formattedOptions,
    });
  }

  isSelected(option: DropdownOption): boolean {
    return this.selectedOptions.some(opt => opt.value === option.value);
  }

  onSearchChange() {
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter(opt =>
      opt.label.toLowerCase().includes(term)
    );
  }

  get noResults(): boolean {
    return this.allowSearch && this.searchTerm.trim().length > 0 && this.filteredOptions.length === 0;
  }

  isOthersOption(option: DropdownOption): boolean {
    if (!this.question?.questionOptions) return false;
    const originalOption = this.question.questionOptions.find(opt => opt.optionId === option.value);
    return originalOption?.text?.toLowerCase().trim() === 'others';
  }

  isOthersSelected(optionId: string): boolean {
    return this.othersOptionIds.has(optionId) && this.selectedOptions.some(opt => opt.value === optionId);
  }

  private findOthersOptionIds(): Set<string> {
    const ids = new Set<string>();
    if (!this.question?.questionOptions) return ids;
    this.question.questionOptions.forEach(opt => {
      if (opt.text?.toLowerCase().trim() === 'others') {
        ids.add(opt.optionId);
      }
    });
    return ids;
  }

  onOthersTextChange(optionId: string, text: string) {
    this.othersTextMap.set(optionId, text);
    // Only emit answer, don't update form control to prevent re-render issues
    this.emitAnswer();
  }

  onOthersInputBlur() {
    // Ensure answer is emitted when user leaves the field
    this.emitAnswer();
    this.onTouched();
  }

  getOthersText(optionId: string): string {
    return this.othersTextMap.get(optionId) || '';
  }
}
